import Box from '@mui/material/Box';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { captureError } from '../utils/capture-error';
import { AllianceErrorBoundary } from './alliance/error-boundary';
import { SectionErrorScreen } from './alliance/system-screens';

/**
 * Wraps one section (a tab, a panel, a drawer) so a render error there
 * shows the compact "This section didn't load." with Try again, and the rest
 * of the page keeps working. Moving to another page clears the error; key it
 * by tab to clear it when the tab changes.
 *
 * @param dense - Inside a narrow panel or drawer
 * @param floating - For fixed overlays (the Arrival bar and its dialogs),
 *   whose fallback would otherwise land at the bottom of the page: the error
 *   shows as a card fixed above the bottom of the screen instead
 */
export function SectionErrorBoundary(props: {
  children: React.ReactNode;
  dense?: boolean;
  floating?: boolean;
}) {
  // Hooks
  const location = useLocation();

  return (
    <AllianceErrorBoundary
      resetKey={location.pathname}
      onError={(error, errorInfo) =>
        captureError({ data: { error, errorInfo } })
      }
      fallback={({ reset }) =>
        props.floating ? (
          <Box
            sx={{
              position: 'fixed',
              // Above the Arrival bar, clear of the bottom-left buttons
              bottom: 88,
              left: { xs: 16, md: 'auto' },
              right: 16,
              width: { md: 380 },
              zIndex: (theme) => theme.zIndex.snackbar,
              backgroundColor: '#ffffff',
              borderRadius: '14px',
              boxShadow: '0 12px 32px #0000003d',
            }}
          >
            <SectionErrorScreen onRetry={reset} dense />
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <SectionErrorScreen onRetry={reset} dense={props.dense} />
          </Box>
        )
      }
    >
      {props.children}
    </AllianceErrorBoundary>
  );
}
